import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Calendar, Check, X as XIcon, Save, Trash2, Search, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const PGAttendance = ({ group, members }) => {
  const { user, permissions } = useContext(AuthContext);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attendanceToDelete, setAttendanceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  
  // Edit attendance state
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [editPresentMembers, setEditPresentMembers] = useState([]);
  const [editMemberSearch, setEditMemberSearch] = useState('');
  const [saving, setSaving] = useState(false);
  
  // New attendance form
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [presentMembers, setPresentMembers] = useState([]);
  const [notes, setNotes] = useState('');

  // Check permissions
  const canDeleteAttendance = permissions?.includes('small_groups.attendance_delete') || user?.role === 'admin';
  const canEditAttendance = permissions?.includes('small_groups.attendance_edit') || user?.role === 'admin' || user?.role === 'lider_pg';

  useEffect(() => {
    if (group) {
      fetchAttendanceHistory();
    }
  }, [group]);

  const fetchAttendanceHistory = async () => {
    try {
      const response = await axios.get(`${API}/small-groups/${group.id}/attendance`);
      setAttendanceHistory(response.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico de presença');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMember = (memberId) => {
    setPresentMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSaveAttendance = async () => {
    if (presentMembers.length === 0) {
      toast.error('Selecione pelo menos um membro presente');
      return;
    }

    try {
      await axios.post(`${API}/small-groups/${group.id}/attendance`, {
        meeting_date: meetingDate,
        present_member_ids: presentMembers,
        notes: notes
      });
      
      toast.success('Presença registrada com sucesso!');
      
      // Reset form
      setMeetingDate(new Date().toISOString().split('T')[0]);
      setPresentMembers([]);
      setNotes('');
      setMarkingAttendance(false);
      
      // Refresh history
      fetchAttendanceHistory();
    } catch (error) {
      toast.error('Erro ao salvar presença');
    }
  };

  const handleDeleteClick = (attendance) => {
    setAttendanceToDelete(attendance);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!attendanceToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/small-groups/${group.id}/attendance/${attendanceToDelete.id}`);
      toast.success('Registro de presença excluído com sucesso!');
      fetchAttendanceHistory();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao excluir registro de presença';
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setAttendanceToDelete(null);
    }
  };

  const handleEditClick = (attendance) => {
    setEditingAttendance(attendance);
    setEditPresentMembers(attendance.present_member_ids || attendance.present_members?.map(m => m.id) || []);
    setEditMemberSearch('');
  };

  const handleToggleEditMember = (memberId) => {
    setEditPresentMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSaveEdit = async () => {
    if (!editingAttendance) return;

    setSaving(true);
    try {
      // Calculate absent members
      const allMemberIds = members?.map(m => m.id) || [];
      const absentMemberIds = allMemberIds.filter(id => !editPresentMembers.includes(id));

      await axios.put(`${API}/small-groups/${group.id}/attendance/${editingAttendance.id}`, {
        present_member_ids: editPresentMembers,
        absent_member_ids: absentMemberIds
      });
      
      toast.success('Registro de presença atualizado com sucesso!');
      setEditingAttendance(null);
      fetchAttendanceHistory();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao atualizar registro de presença';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAttendance(null);
    setEditPresentMembers([]);
    setEditMemberSearch('');
  };

  return (
    <div className="space-y-6">
      {/* Mark New Attendance */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-bold text-lg">Registrar Nova Presença</h4>
          {!markingAttendance ? (
            <Button 
              onClick={() => setMarkingAttendance(true)}
              className="btn-primary"
              data-testid="start-marking-attendance"
            >
              <Calendar size={18} className="mr-2" />
              Nova Presença
            </Button>
          ) : (
            <Button 
              onClick={() => setMarkingAttendance(false)}
              variant="outline"
            >
              Cancelar
            </Button>
          )}
        </div>

        {markingAttendance && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Data da Reunião</Label>
              <Input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                data-testid="meeting-date-input"
              />
            </div>

            <div>
              <Label className="mb-3 block">Marcar Presença dos Membros</Label>
              
              {/* Campo de busca */}
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Buscar membro por nome..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="pl-10"
                  data-testid="member-search-input"
                />
              </div>
              
              {/* Contador de presentes/total */}
              <div className="flex justify-between items-center mb-3 text-sm">
                <span className="text-gray-500">
                  {members?.filter(m => m.name?.toLowerCase().includes(memberSearch.toLowerCase())).length || 0} membro(s) encontrado(s)
                </span>
                <span className="font-medium text-green-600">
                  {presentMembers.length} presente(s) de {members?.length || 0}
                </span>
              </div>
              
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {members && members
                  .filter(member => member.name?.toLowerCase().includes(memberSearch.toLowerCase()))
                  .map((member) => (
                  <div 
                    key={member.id} 
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition ${
                      presentMembers.includes(member.id) 
                        ? 'bg-green-100 border-2 border-green-400' 
                        : 'bg-white border-2 border-gray-200'
                    }`}
                    onClick={() => handleToggleMember(member.id)}
                    data-testid="member-attendance-item"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        presentMembers.includes(member.id) 
                          ? 'bg-green-500' 
                          : 'bg-gray-300'
                      }`}>
                        {presentMembers.includes(member.id) ? (
                          <Check size={20} className="text-white" />
                        ) : (
                          <XIcon size={20} className="text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.age} anos • {member.phone}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-semibold ${
                      presentMembers.includes(member.id) ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {presentMembers.includes(member.id) ? 'PRESENTE' : 'AUSENTE'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Observações (opcional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: Reunião sobre liderança, tema do dia, etc..."
                rows={3}
                data-testid="notes-textarea"
              />
            </div>

            <Button 
              onClick={handleSaveAttendance}
              className="btn-primary w-full"
              data-testid="save-attendance-button"
            >
              <Save size={18} className="mr-2" />
              Salvar Presença
            </Button>
          </div>
        )}
      </div>

      {/* Attendance History */}
      <div className="card">
        <h4 className="font-bold text-lg mb-4">Histórico de Presenças</h4>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Carregando...</div>
        ) : attendanceHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
            <p>Nenhuma presença registrada ainda</p>
          </div>
        ) : (
          <div className="space-y-4">
            {attendanceHistory.map((attendance) => (
              <div key={attendance.id} className="border rounded-lg p-4" data-testid="attendance-history-item">
                {editingAttendance?.id === attendance.id ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="font-bold text-gray-900">
                        Editando: {format(parseISO(attendance.meeting_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={saving}
                        >
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={saving}
                          className="btn-primary"
                        >
                          <Save size={14} className="mr-1" />
                          {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </div>
                    </div>

                    {/* Search field for edit */}
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Buscar membro por nome..."
                        value={editMemberSearch}
                        onChange={(e) => setEditMemberSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {/* Member count */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">
                        {members?.filter(m => m.name?.toLowerCase().includes(editMemberSearch.toLowerCase())).length || 0} membro(s)
                      </span>
                      <span className="font-medium text-green-600">
                        {editPresentMembers.length} presente(s) de {members?.length || 0}
                      </span>
                    </div>

                    {/* Members list for editing */}
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {members && members
                        .filter(member => member.name?.toLowerCase().includes(editMemberSearch.toLowerCase()))
                        .map((member) => (
                        <div 
                          key={member.id} 
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition ${
                            editPresentMembers.includes(member.id) 
                              ? 'bg-green-100 border-2 border-green-400' 
                              : 'bg-white border-2 border-gray-200'
                          }`}
                          onClick={() => handleToggleEditMember(member.id)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              editPresentMembers.includes(member.id) 
                                ? 'bg-green-500' 
                                : 'bg-gray-300'
                            }`}>
                              {editPresentMembers.includes(member.id) ? (
                                <Check size={20} className="text-white" />
                              ) : (
                                <XIcon size={20} className="text-white" />
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{member.name}</p>
                              <p className="text-sm text-gray-500">{member.age} anos</p>
                            </div>
                          </div>
                          <span className={`text-sm font-semibold ${
                            editPresentMembers.includes(member.id) ? 'text-green-600' : 'text-gray-400'
                          }`}>
                            {editPresentMembers.includes(member.id) ? 'PRESENTE' : 'AUSENTE'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-bold text-gray-900">
                          {format(parseISO(attendance.meeting_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                        </p>
                        <p className="text-sm text-gray-500">
                          {attendance.present_members?.length || 0} presente(s) • {attendance.absent_members?.length || 0} ausente(s)
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="text-lg font-bold text-green-600">
                            {Math.round((attendance.present_members?.length || 0) / ((attendance.present_members?.length || 0) + (attendance.absent_members?.length || 0)) * 100)}%
                          </span>
                          <p className="text-xs text-gray-500">presença</p>
                        </div>
                        {canEditAttendance && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(attendance)}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            data-testid="edit-attendance-button"
                          >
                            <Edit size={16} />
                          </Button>
                        )}
                        {canDeleteAttendance && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(attendance)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-attendance-button"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>

                    {attendance.notes && (
                      <div className="mb-3 p-2 bg-yellow-50 rounded text-sm text-gray-700">
                        <strong>Obs:</strong> {attendance.notes}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm font-semibold text-green-700 mb-2">Presentes:</p>
                        <div className="space-y-1">
                          {attendance.present_members?.map((member) => (
                            <div key={member.id} className="text-sm text-gray-700 flex items-center">
                              <Check size={14} className="text-green-600 mr-1" />
                              {member.name}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-red-700 mb-2">Ausentes:</p>
                        <div className="space-y-1">
                          {attendance.absent_members?.map((member) => (
                            <div key={member.id} className="text-sm text-gray-700 flex items-center">
                              <XIcon size={14} className="text-red-600 mr-1" />
                              {member.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Registro de Presença</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este registro de presença do dia{' '}
              <strong>
                {attendanceToDelete && format(parseISO(attendanceToDelete.meeting_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </strong>?
              <br /><br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PGAttendance;
